import { Link } from 'react-router-dom';
import { AssignmentStatus, type HomeworkAssignmentSummary } from '@tutor/shared';
import { Avatar, Badge, Button, cn } from '@tutor/ui';
import { formatAgo, formatDue, formatScore } from '../lib/format';

const STATUS: Record<AssignmentStatus, { label: string; tone: 'neutral' | 'info' | 'warning' | 'success' | 'danger' }> = {
  ASSIGNED: { label: 'Not started', tone: 'neutral' },
  IN_PROGRESS: { label: 'In progress', tone: 'info' },
  SUBMITTED: { label: 'Submitted', tone: 'warning' },
  MARKED: { label: 'Marked', tone: 'success' },
  RETURNED: { label: 'Returned', tone: 'success' },
  OVERDUE: { label: 'Overdue', tone: 'danger' },
};

export interface HomeworkCardProps {
  assignment: HomeworkAssignmentSummary;
  perspective: 'tutor' | 'student';
  className?: string;
}

export function HomeworkCard({ assignment, perspective, className }: HomeworkCardProps) {
  const basePath = perspective === 'tutor' ? '/tutor' : '/student';
  const status = STATUS[assignment.status];
  const due = formatDue(assignment.dueAt);
  const isMarked = assignment.status === AssignmentStatus.MARKED || assignment.status === AssignmentStatus.RETURNED;

  // The primary action is whatever moves this piece of work forward for this role.
  const actionLabel =
    perspective === 'tutor'
      ? assignment.status === AssignmentStatus.SUBMITTED
        ? 'Mark Homework'
        : 'View'
      : assignment.status === AssignmentStatus.IN_PROGRESS
        ? 'Continue Homework'
        : isMarked
          ? 'View Feedback'
          : 'View Homework';

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 transition-colors hover:border-slate-300',
        className,
      )}
    >
      {perspective === 'tutor' && (
        <Avatar
          name={assignment.student.fullName}
          src={assignment.student.avatarUrl}
          size="md"
          className="hidden sm:block"
        />
      )}

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <Link
            to={`${basePath}/homework/${assignment.id}`}
            className="truncate font-medium text-slate-900 hover:text-brand-700"
          >
            {perspective === 'tutor' ? assignment.student.fullName : assignment.homeworkTitle}
          </Link>
          {assignment.subject && <Badge tone="brand">{assignment.subject.name}</Badge>}
          <Badge tone={status.tone}>{status.label}</Badge>
        </div>

        <p className="mt-0.5 truncate text-sm text-slate-600">
          {perspective === 'tutor' ? assignment.homeworkTitle : `Set by ${assignment.assignedBy.fullName}`}
        </p>

        <p className="mt-1 text-xs text-slate-500">
          {assignment.submittedAt ? (
            <>Submitted {formatAgo(assignment.submittedAt)}</>
          ) : (
            <span className={cn(due.tone === 'danger' && 'font-medium text-rose-600', due.tone === 'warning' && 'font-medium text-amber-600')}>
              {due.label}
            </span>
          )}
          {isMarked && assignment.score !== null && (
            <span className="ml-2 font-medium text-emerald-700">
              {formatScore(assignment.score, assignment.maxScore)}
            </span>
          )}
        </p>
      </div>

      <div className="ml-auto shrink-0">
        <Link to={`${basePath}/homework/${assignment.id}`}>
          <Button
            size="sm"
            variant={
              perspective === 'tutor' && assignment.status === AssignmentStatus.SUBMITTED
                ? 'primary'
                : 'outline'
            }
          >
            {actionLabel}
          </Button>
        </Link>
      </div>
    </div>
  );
}
